import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACK_KEY = 'support_new_msg_ack';
const POLL_MS = 20000;

export const NewMessageAlert = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const latestRef = useRef(null);

  const checkMessages = useCallback(async () => {
    // The Support Chat page itself shows/reads messages — no popup there.
    if (window.location.pathname.includes('/admin/support')) return;
    try {
      const res = await axios.get(`${API}/admin/support/unread-total`, getAuthHeader());
      const { unread_count, latest_user_message_at } = res.data || {};
      if (!unread_count || !latest_user_message_at) return;
      latestRef.current = latest_user_message_at;
      const acked = localStorage.getItem(ACK_KEY);
      if (!acked || latest_user_message_at > acked) {
        setOpen(true);
      }
    } catch {
      // silent — polling should never surface errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkMessages();
    const id = setInterval(checkMessages, POLL_MS);
    return () => clearInterval(id);
  }, [checkMessages]);

  // Entering Support Chat counts as acknowledging the alert.
  useEffect(() => {
    if (location.pathname.includes('/admin/support')) {
      if (latestRef.current) localStorage.setItem(ACK_KEY, latestRef.current);
      setOpen(false);
    }
  }, [location.pathname]);

  const acknowledge = () => {
    if (latestRef.current) localStorage.setItem(ACK_KEY, latestRef.current);
    setOpen(false);
  };

  const viewMessage = () => {
    acknowledge();
    navigate('/admin/support');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) acknowledge(); }}>
      <DialogContent className="max-w-sm" data-testid="new-message-alert-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-600">
            <MessageCircle size={20} />
            You have a new message!
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">
          A user has sent or responded to a message in Support Chat.
        </p>
        <div className="flex gap-3 justify-end mt-2">
          <Button variant="outline" onClick={acknowledge} data-testid="new-message-dismiss-btn">
            Dismiss
          </Button>
          <Button onClick={viewMessage} className="bg-blue-600 hover:bg-blue-700" data-testid="new-message-view-btn">
            <MessageCircle size={15} className="mr-1.5" />
            View Message
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewMessageAlert;
